import { useState } from "react";
import { Bot, User, Sparkles, FileDown, Check, Download } from "lucide-react";

function buildRunbook(message, precedingPrompt) {
  const ts = new Date(message.ts || Date.now()).toISOString();
  const promptLine = precedingPrompt ? precedingPrompt.trim() : "(no preceding prompt)";
  return (
    `# DefDossier — SENTINEL Runbook\n\n` +
    `**Generated:** ${ts}\n` +
    `**Source:** Sentinel AI (DefDossier purple-team assistant)\n\n` +
    `---\n\n` +
    `## Operator Query\n\n` +
    `> ${promptLine.replace(/\n/g, "\n> ")}\n\n` +
    `---\n\n` +
    `## Sentinel Response\n\n` +
    `${message.content}\n\n` +
    `---\n\n` +
    `_Exported from DefDossier. Use within Rules of Engagement of your authorised target/lab._\n`
  );
}

function downloadFile(filename, content) {
  const blob = new Blob([content], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 0);
}

function RunbookActions({ message, precedingPrompt }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    const md = buildRunbook(message, precedingPrompt);
    try {
      await navigator.clipboard.writeText(md);
    } catch {
      // Fallback for environments without clipboard API (e.g. http).
      const ta = document.createElement("textarea");
      ta.value = md;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const onDownload = () => {
    const stamp = new Date(message.ts || Date.now()).toISOString().slice(0, 19).replace(/[:T]/g, "-");
    downloadFile(`defdossier-runbook-${stamp}.md`, buildRunbook(message, precedingPrompt));
  };

  return (
    <div className="mt-2 flex items-center gap-2">
      <button
        onClick={onCopy}
        className="inline-flex items-center gap-1 border border-[var(--border)] hover:border-[var(--cyan)] hover:text-[var(--cyan)] px-2 py-1 text-[10px] tracking-widest uppercase transition-colors"
        data-testid={`runbook-copy-${message._k}`}
        title="Copy this answer as a Markdown runbook"
      >
        {copied ? <Check size={11} strokeWidth={2} /> : <FileDown size={11} strokeWidth={1.5} />}
        {copied ? "Copied" : "Copy as runbook"}
      </button>
      <button
        onClick={onDownload}
        className="inline-flex items-center gap-1 border border-[var(--border)] hover:border-[var(--cyan)] hover:text-[var(--cyan)] px-2 py-1 text-[10px] tracking-widest uppercase transition-colors"
        data-testid={`runbook-download-${message._k}`}
        title="Download as .md file"
      >
        <Download size={11} strokeWidth={1.5} />
        .md
      </button>
    </div>
  );
}

function MessageRow({ message, idx, precedingPrompt }) {
  const isUser = message.role === "user";
  const showRunbook = !isUser && !message.streaming && !message.retrying
    && message.content && !message.content.startsWith("[SENTINEL OFFLINE]")
    && !message.content.startsWith("[DEMO LIMIT]");
  return (
    <div className="flex gap-3 items-start" data-testid={`msg-${message.role}-${idx}`}>
      <div className="shrink-0 mt-0.5">
        {isUser
          ? <User size={16} color="var(--amber)" strokeWidth={1.5} />
          : <Bot size={16} color="var(--cyan)" strokeWidth={1.5} />}
      </div>
      <div className="flex-1">
        <div className={`text-[10px] tracking-widest uppercase ${isUser ? "text-[var(--amber)]" : "text-[var(--cyan)]"}`}>
          {isUser ? "// OPERATOR" : "// SENTINEL"}
          {message.streaming && <span className="ml-2 text-[var(--green)] blink">▌</span>}
          {message.retrying && <span className="ml-2 text-[var(--amber)]">// reconnecting…</span>}
        </div>
        <pre className="whitespace-pre-wrap text-[var(--text)] leading-relaxed mt-0.5 font-mono text-sm">
          {message.content}
        </pre>
        {message.cta === "request-access" && (
          <a
            href="/?access=1"
            className="inline-flex items-center mt-2 px-3 py-1.5 border border-[var(--cyan)] text-[var(--cyan)] hover:bg-[var(--cyan)] hover:text-black text-xs font-mono uppercase tracking-widest transition-colors"
            data-testid="demo-limit-cta"
          >
            Request Full Access →
          </a>
        )}
        {showRunbook && <RunbookActions message={message} precedingPrompt={precedingPrompt} />}
      </div>
    </div>
  );
}

function SuggestionTiles({ items, onPick, testidPrefix, label }) {
  return (
    <div className="mt-2">
      <div className="text-[10px] tracking-widest uppercase text-[var(--text-2)] mb-2 flex items-center gap-1">
        <Sparkles size={11} color="var(--cyan)" strokeWidth={1.5} />
        {label}
      </div>
      <div className="grid sm:grid-cols-2 gap-2">
        {items.map((s, i) => (
          <button
            key={`${s}-${i}`}
            onClick={() => onPick(s)}
            className="text-left border border-[var(--border)] p-3 hover:border-[var(--cyan)] hover:text-white text-xs transition-colors"
            data-testid={`${testidPrefix}-${i}`}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function MessageList({
  messages,
  suggestions,
  followups,
  followupsLoading,
  onPick,
  endRef,
  showConnecting,
  busy,
}) {
  const hasMessages = messages.length > 0;
  const showDynamic = hasMessages && !busy && followups.length > 0;
  const showDynamicLoading = hasMessages && !busy && followupsLoading && followups.length === 0;

  return (
    <div className="p-5 h-[480px] overflow-y-auto space-y-4 font-mono text-sm" data-testid="chat-window">
      {!hasMessages && (
        <div className="text-[var(--text-2)] space-y-3">
          <div>SENTINEL online. Awaiting first input.</div>
          <SuggestionTiles
            items={suggestions}
            onPick={onPick}
            testidPrefix="suggest-starter"
            label="SUGGESTED PROMPTS"
          />
        </div>
      )}
      {messages.map((m, i) => {
        // For assistant messages, find the most recent preceding USER
        // message so we can attribute the runbook to the right query.
        let precedingPrompt = "";
        if (m.role === "assistant") {
          for (let j = i - 1; j >= 0; j--) {
            if (messages[j].role === "user") {
              precedingPrompt = messages[j].content;
              break;
            }
          }
        }
        return <MessageRow key={m._k} message={m} idx={i} precedingPrompt={precedingPrompt} />;
      })}
      {showConnecting && (
        <div className="flex gap-3 items-center text-[var(--cyan)]" data-testid="chat-thinking">
          <Bot size={16} strokeWidth={1.5} />
          <span className="blink">// SENTINEL is connecting █</span>
        </div>
      )}
      {showDynamicLoading && (
        <div className="text-[10px] tracking-widest uppercase text-[var(--text-2)] flex items-center gap-1" data-testid="followups-loading">
          <Sparkles size={11} color="var(--cyan)" strokeWidth={1.5} />
          GENERATING FOLLOW-UPS…
        </div>
      )}
      {showDynamic && (
        <div data-testid="followups-tiles">
          <SuggestionTiles
            items={followups}
            onPick={onPick}
            testidPrefix="suggest-followup"
            label="SUGGESTED FOLLOW-UPS"
          />
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
